import { useApiClient, invalidateResource, type components } from '@esnafai/shared/api';
import { AppShell, NotificationBell } from '@esnafai/shared/ui';
import { useSession } from '@esnafai/shared/auth';
import { useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import { useMatches } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useActionError } from '@/components/useActionError/useActionError';
import { t } from '@esnafai/shared/i18n';
import { Banners } from './Banners';
import { EnvBadge } from './EnvBadge';
import { moreNav, primaryNav, type NavEntry } from './nav';
import { useHome } from './use-home';
import { useUserMenu } from './UserMenu';

type NotificationPage = components['schemas']['NotificationPage'];
type MarkReadRequest = components['schemas']['MarkReadRequest'];

const notificationsKey = ['get', '/api/notifications'];

type NotificationCache = NotificationPage | InfiniteData<NotificationPage>;

// The page with the given notifications (or all of them) read, and its unread count lowered by those it changed.
function markedRead(
  page: NotificationPage,
  request: MarkReadRequest,
  now: string,
): NotificationPage {
  let changed = 0;
  const items = page.items.map((notification) => {
    const hit = request.all === true || (request.ids ?? []).includes(notification.notification_id);
    if (!hit || notification.read_at != null) return notification;
    changed += 1;
    return { ...notification, read_at: now };
  });
  const unread = request.all === true ? 0 : Math.max(0, page.unread_count - changed);
  return { ...page, items, unread_count: unread };
}

// The bell (merchant screens §2, §3.13): unread_count and the newest of GET /notifications; the stream's
// notification.created puts a new one on top. Opening a row marks it read at once (an optimistic update, UI
// architecture §4); a failed POST rolls it back with the error toast (§4.2). Its link navigates.
function Bell() {
  const { $api } = useApiClient();
  const queryClient = useQueryClient();
  const actionError = useActionError();
  const { principal } = useSession();
  const list = $api.useQuery('get', '/api/notifications', {}, { enabled: principal !== null });
  const markRead = $api.useMutation('post', '/api/notifications/read', {
    onMutate: async ({ body }) => {
      await queryClient.cancelQueries({ queryKey: notificationsKey });
      const previous = queryClient.getQueriesData<NotificationCache>({
        queryKey: notificationsKey,
      });
      const now = new Date().toISOString();
      // The bell reads one page; /notifications keeps its cursor list (pages) under the same path.
      queryClient.setQueriesData<NotificationCache>({ queryKey: notificationsKey }, (data) => {
        if (!data) return data;
        return 'pages' in data
          ? { ...data, pages: data.pages.map((page) => markedRead(page, body, now)) }
          : markedRead(data, body, now);
      });
      return { previous };
    },
    onError: (error, _variables, context) => {
      const previous = (context as { previous?: [QueryKey, unknown][] } | undefined)?.previous;
      for (const [key, page] of previous ?? []) queryClient.setQueryData(key, page);
      actionError(error);
    },
    onSettled: () => invalidateResource(queryClient, '/api/notifications'),
  });
  return (
    <NotificationBell
      unreadCount={list.data?.unread_count ?? 0}
      notifications={list.data?.items}
      loading={list.isPending}
      error={list.isError ? { onRetry: () => void list.refetch() } : null}
      onOpenNotification={(notification) => {
        if (notification.read_at == null) {
          markRead.mutate({ body: { ids: [notification.notification_id] } });
        }
      }}
      onMarkAllRead={() => markRead.mutate({ body: { all: true } })}
      markingAllRead={markRead.isPending}
      allHref="/notifications"
    />
  );
}

// The merchant panel's AppShell around every signed-in, non-onboarding route (merchant screens §1 rule 1, §2).
export function MerchantShell({ children }: { children: ReactNode }) {
  const matches = useMatches();
  const activeNavId = matches.reduce<string | undefined>(
    (found, match) => match.staticData?.nav ?? found,
    undefined,
  );
  const waiting = useHome().data?.waiting.count;
  const item = (entry: NavEntry) => ({
    id: entry.id,
    to: entry.to,
    label: t(entry.label),
    icon: entry.icon,
    badge: entry.id === 'waiting' ? waiting : undefined,
  });
  return (
    <AppShell
      primaryNav={primaryNav.map(item)}
      moreNav={moreNav.map(item)}
      activeNavId={activeNavId}
      homeTo="/"
      header={<EnvBadge />}
      notificationBell={<Bell />}
      userMenu={useUserMenu()}
      banners={<Banners />}
    >
      {children}
    </AppShell>
  );
}
